import { SagaIterator, Task } from 'redux-saga';
import { put, call, fork, select, takeLatest, cancel } from 'redux-saga/effects';
import { clearNotifications } from '../../../../common/navigation/state/actions/index';
import { getSpotId } from 'app/scripts/services/app-state';
import count from '../../api/count';
import { Folder, ContentType } from '../types';
import { State } from 'state/store-types';
import {
  startPostsRealtimeCounter,
  cancelPostsRealtimeCounter,
  fetchCountSuccess,
  fetchCountFailure
} from './index';

const POLLING_DELAY = 60 * 1000;

const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

const lastFetchSelector = (state: State): number => state.moderation?.messages?.lastFetch;

export function* cancelPolling(pollTask) {
  yield cancel(pollTask);
  yield put(clearNotifications({ notificationType: 'posts' }));
}

export function* fetchCount({
  folder,
  polling = false,
  contentType = 'COMMENTS'
}: {
  folder?: Folder;
  polling: boolean;
  contentType: ContentType;
}): SagaIterator {
  const spotId: string = getSpotId();
  let from = null;

  if (polling) {
    from = yield select(lastFetchSelector);
  }

  try {
    const { total_items: counts } = yield call(count, { folder, from, spotId, contentType });
    yield put(fetchCountSuccess({ counts, polling, contentType }));
  } catch (error) {
    // reportError
    console.error(error);
    yield put(fetchCountFailure({ error }));
  }
}

// Worker sagas
export function* pollRealtimeCount(): SagaIterator {
  while (true) {
    yield call(delay, POLLING_DELAY);
    yield fork(fetchCount, { polling: true, folder: Folder.PENDING, contentType: 'POSTS' });
  }
}

// This method initializes the polling loop that does two things:
// 1. Creates a forked task from the poll API call
// 2. Passes in the forked task as an argument to a watching function that will cancel a task
export function* pollOrCancelRealtimeCount() {
  const pollTask = yield fork(pollRealtimeCount);
  yield watchCancelRealtimeCount(pollTask);
}

// Watcher sagas
export function* watchCancelRealtimeCount(pollTask) {
  yield takeLatest(cancelPostsRealtimeCounter.type, () => cancelPolling(pollTask));
}

export function* watchRealtimeCount() {
  yield takeLatest(startPostsRealtimeCounter.type, pollOrCancelRealtimeCount);
}

// Main saga
export default function* realtimeCountSaga() {
  yield watchRealtimeCount();
}
